import { AddFilterButton, Filters } from '@/components'
import {
  Card,
  CardContent,
  CardHeading,
  ChartFilters,
  FigureLabel,
  FigureValue,
} from '@/components/ui'
import {
  applyFilters,
  type FieldSchema,
  FiltersProvider,
  useFilters,
} from '@/stores'
import {
  chartColors,
  chartPalette,
  cn,
  numberToCompactCurrency,
  numberToCurrency,
  percentFromRate,
} from '@/utils'
import { useSecuritiesReadState } from '../../hooks/project'
import { formatAssetClass } from '../../labels'
import { noTotalNotes } from '../portfolio/AllAccountsCard/heroText'
import { LedgerIncompleteMark } from '../portfolio/AllAccountsCard/LedgerIncompleteMark'
import { monthLabel } from '../portfolio/model/months'
import {
  type AccountAsOf,
  type AllAccountsAsOf,
  accountsAsOf,
  type MonthTotal,
  type PortfolioOverview,
} from '../portfolio/model/portfolioOverview'
import type { AccountMonth, MonthKey } from '../portfolio/model/types'
import { longDate } from '../portfolio/portfolioText'
import { SecuritiesFailedState } from '../portfolio/SecuritiesFailedState'
import { SecuritiesPendingState } from '../portfolio/SecuritiesPendingState'
import type { Holding, PortfolioAccount } from '../portfolio/types'
import { isPosition, snapshotTotals } from '../portfolio/usePortfolioDashboard'
import { usePortfolioOverview } from '../portfolio/usePortfolioOverview'
import { useProjectSecurities } from '../portfolio/useProjectSecurities'
import { ValueTrendChart } from '../portfolio/ValueTrendChart'
import { OverviewBarRow } from './OverviewBarRow'
import { OverviewViewLink } from './OverviewViewLink'

interface OverviewPortfolioProps {
  slug: string
}

// How many asset classes to name before the rest fold into "Other".
const MAX_CLASSES = 5

const PORTFOLIO_FILTER_CONTEXT_ID = 'overview-portfolio'

// Same account filter the cash sections use, so the portfolio narrows the same way.
const ACCOUNT_FILTER_SCHEMA: FieldSchema[] = [
  { field: 'accountNumber', label: 'Account', type: 'select', multiple: true },
]

const sumOf = <Item,>(items: Item[], pick: (item: Item) => number): number =>
  items.reduce((running, item) => running + pick(item), 0)

const centsSum = (values: (number | null)[]): number =>
  values.reduce<number>(
    (total, value) => total + Math.round((value ?? 0) * 100),
    0,
  ) / 100

type BlockingLists = Pick<
  MonthTotal,
  'missing' | 'verifying' | 'midCycle' | 'unknownValue' | 'twoStatements'
>

// A verifying statement no account claims (account id '') still blocks any choice of accounts.
const blockingFor = (
  asOf: AllAccountsAsOf,
  accountIds: Set<string>,
): BlockingLists => {
  const isChosen = (entry: AccountMonth) =>
    entry.accountId === '' || accountIds.has(entry.accountId)
  return {
    missing: asOf.missing.filter(isChosen),
    verifying: asOf.verifying.filter(isChosen),
    midCycle: asOf.midCycle.filter(isChosen),
    unknownValue: asOf.unknownValue.filter(isChosen),
    twoStatements: asOf.twoStatements.filter(isChosen),
  }
}

// The Portfolio hero's rule over the chosen accounts: one account without a counted statement
// that month gives "No total". With every account chosen it is the hero's own figure.
export const monthTotalOf = (
  asOf: AllAccountsAsOf,
  accountIds: Set<string>,
): MonthTotal => {
  if (asOf.accounts.every((account) => accountIds.has(account.accountId))) {
    return asOf
  }
  const blocking = blockingFor(asOf, accountIds)
  const chosen = asOf.accounts.filter((account) =>
    accountIds.has(account.accountId),
  )
  const hasTotal =
    chosen.length > 0 &&
    Object.values(blocking).every((entries) => entries.length === 0)
  return {
    month: asOf.month,
    asOfDate: asOf.asOfDate,
    total: hasTotal ? centsSum(chosen.map((account) => account.value)) : null,
    securities: hasTotal
      ? centsSum(chosen.map((account) => account.securities))
      : null,
    cash: hasTotal ? centsSum(chosen.map((account) => account.cash)) : null,
    cashIncluded: chosen.every(
      (account) => account.status === 'ended' || account.cash != null,
    ),
    accountCount: chosen.length,
    ...blocking,
  }
}

// The holdings of each chosen account's statement for the month, the ones the total counts.
const holdingsCountedIn = (
  accounts: AccountAsOf[],
  securities: PortfolioAccount[],
  month: MonthKey,
): Holding[] => {
  const counted = new Set(
    accounts
      .filter((account) => account.status === 'statement')
      .map((account) => account.accountId),
  )
  return securities
    .filter((account) => counted.has(account.id))
    .flatMap(
      (account) =>
        account.periods.find((period) => period.period === month)?.holdings ??
        [],
    )
}

interface AllocationSlice {
  label: string
  value: number
}

const allocationOf = (
  holdings: Holding[],
  cash: number | null,
): AllocationSlice[] => {
  const valueByClass = new Map<string, number>()
  for (const holding of holdings) {
    const label = formatAssetClass(holding.assetClass || 'Unclassified')
    valueByClass.set(
      label,
      (valueByClass.get(label) ?? 0) + (holding.marketValue ?? 0),
    )
  }
  // The month's cash is already only what the holdings do not carry (a sweep balance).
  if (cash != null && cash > 0) {
    valueByClass.set('Cash', (valueByClass.get('Cash') ?? 0) + cash)
  }
  return Array.from(valueByClass.entries())
    .map(([label, value]) => ({ label, value }))
    .filter((slice) => slice.value > 0)
    .sort((first, second) => second.value - first.value)
}

interface TrendPoint {
  key: string
  label: string
  value: number
}

const trendOf = (totals: MonthTotal[]): TrendPoint[] =>
  totals.flatMap((month) =>
    month.total == null
      ? []
      : [
          {
            key: month.month,
            label: monthLabel(month.month),
            value: month.total,
          },
        ],
  )

export interface PortfolioCardFigures {
  month: MonthTotal
  notes: string[]
  costBasis: number | null
  accountCount: number
  verifiedCount: number
  allocation: AllocationSlice[]
  trend: TrendPoint[]
}

// Everything the card shows, read for the latest statement month the way the Portfolio tab
// reads it. Null when no statement has an end date, so there is no month to read.
export const portfolioCardFigures = (
  overview: PortfolioOverview,
  securities: PortfolioAccount[],
  accountIds: Set<string>,
): PortfolioCardFigures | null => {
  if (!overview.latestMonth) return null
  const asOf = accountsAsOf(overview, overview.latestMonth)
  const month = monthTotalOf(asOf, accountIds)
  const chosen = asOf.accounts.filter((account) =>
    accountIds.has(account.accountId),
  )
  const isEveryAccount = month === asOf
  const holdings =
    month.total == null
      ? []
      : holdingsCountedIn(chosen, securities, month.month)
  return {
    month,
    notes: month.total == null ? noTotalNotes(month) : [],
    costBasis: month.total == null ? null : snapshotTotals(holdings).costBasis,
    accountCount: chosen.length,
    verifiedCount: chosen.filter(
      (account) => account.badge.status === 'verified',
    ).length,
    allocation: month.total == null ? [] : allocationOf(holdings, month.cash),
    trend: trendOf(
      isEveryAccount
        ? overview.series
        : overview.months.map((key) =>
            monthTotalOf(accountsAsOf(overview, key), accountIds),
          ),
    ),
  }
}

const positionsAt = (account: PortfolioAccount, month: MonthKey | null) =>
  (
    account.periods.find((period) => period.period === month)?.holdings ?? []
  ).filter(isPosition).length

// Distinct brokerage accounts as filter options, labeled like the account-activity filter.
const buildAccountValues = (
  securities: PortfolioAccount[],
  month: MonthKey | null,
) =>
  securities
    .slice()
    .sort((first, second) =>
      first.accountNumber.localeCompare(second.accountNumber),
    )
    .map((account) => {
      const holdingsCount = positionsAt(account, month)
      return {
        value: account.accountNumber,
        label: account.accountNumber,
        description: `${account.institution} · ${account.accountType} · ${holdingsCount} ${
          holdingsCount === 1 ? 'holding' : 'holdings'
        }`,
      }
    })

// The brokerage side of the case at a glance. Self-hiding: a case with no securities statements
// renders nothing. With portfolios it shows the latest month's total, allocation, and trend.
export const OverviewPortfolio = ({ slug }: OverviewPortfolioProps) => {
  const securities = useProjectSecurities()
  const securitiesReadState = useSecuritiesReadState()

  // No holdings yet: say the brokerage statements are being read or failed to read. Otherwise hide.
  if (securities.length === 0) {
    if (securitiesReadState !== 'reading' && securitiesReadState !== 'failed')
      return null
    return (
      <Card>
        <CardHeading title="Portfolio" />
        <CardContent>
          {securitiesReadState === 'reading' ? (
            <SecuritiesPendingState
              layout="card"
              detail="The system is verifying holdings and value, usually within 24 hours. They appear here when it's done."
            />
          ) : (
            <SecuritiesFailedState layout="card" />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <FiltersProvider contextId={PORTFOLIO_FILTER_CONTEXT_ID}>
      <OverviewPortfolioContent slug={slug} />
    </FiltersProvider>
  )
}

interface MonthFigureProps {
  figures: PortfolioCardFigures | null
  isLedgerIncomplete: boolean
}

const MonthFigure = ({ figures, isLedgerIncomplete }: MonthFigureProps) => {
  const total = figures?.month.total ?? null
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <FigureValue
          className={cn('text-3xl', total == null && 'text-ink-subtle')}
        >
          {total == null ? 'No total' : numberToCurrency(total)}
        </FigureValue>
        {isLedgerIncomplete && <LedgerIncompleteMark />}
      </div>
      <div className="mt-1.5 text-sm text-muted-foreground">
        {figures
          ? `As of ${longDate(figures.month.asOfDate)}`
          : 'No statement has a period end date yet.'}
      </div>
      {figures?.notes.map((note) => (
        <p key={note} className="mt-1 text-xs text-ink-subtle">
          {note}
        </p>
      ))}
    </>
  )
}

const compactOrDash = (value: number | null | undefined) =>
  value == null ? '—' : numberToCompactCurrency(value)

const figureStrip = (figures: PortfolioCardFigures | null) => [
  { label: 'Securities', value: compactOrDash(figures?.month.securities) },
  { label: 'Cash', value: compactOrDash(figures?.month.cash) },
  { label: 'Cost basis', value: compactOrDash(figures?.costBasis) },
  { label: 'Accounts', value: String(figures?.accountCount ?? 0) },
  {
    label: 'Reconciled',
    value: `${figures?.verifiedCount ?? 0} of ${figures?.accountCount ?? 0}`,
  },
]

const Allocation = ({ slices }: { slices: AllocationSlice[] }) => {
  const total = sumOf(slices, (slice) => slice.value) || 1
  const named = slices.slice(0, MAX_CLASSES)
  const otherValue = sumOf(slices.slice(MAX_CLASSES), (slice) => slice.value)
  return (
    <div>
      <FigureLabel className="mb-2">Allocation</FigureLabel>
      <div className="space-y-1">
        {named.map((slice, index) => (
          <OverviewBarRow
            key={slice.label}
            label={slice.label}
            color={chartPalette[index % chartPalette.length]}
            fraction={slice.value / total}
            value={numberToCompactCurrency(slice.value)}
            valueMuted={percentFromRate(slice.value / total)}
          />
        ))}
        {otherValue > 0 ? (
          <OverviewBarRow
            label="Other"
            color={chartColors.gray}
            fraction={otherValue / total}
            value={numberToCompactCurrency(otherValue)}
            valueMuted={percentFromRate(otherValue / total)}
          />
        ) : null}
      </div>
    </div>
  )
}

const OverviewPortfolioContent = ({ slug }: OverviewPortfolioProps) => {
  const overview = usePortfolioOverview()
  const securities = useProjectSecurities()
  const filters = useFilters()

  if (!overview) return null
  const chosenIds = new Set(
    applyFilters(securities, filters, ACCOUNT_FILTER_SCHEMA).map(
      (account) => account.id,
    ),
  )
  const figures = portfolioCardFigures(overview, securities, chosenIds)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filters
          dialogId={PORTFOLIO_FILTER_CONTEXT_ID}
          schema={ACCOUNT_FILTER_SCHEMA}
          getUniqueValues={() =>
            buildAccountValues(securities, overview.latestMonth)
          }
        />
        <ChartFilters>
          <AddFilterButton
            dialogId={PORTFOLIO_FILTER_CONTEXT_ID}
            schema={ACCOUNT_FILTER_SCHEMA}
          />
        </ChartFilters>
      </div>

      <Card>
        <CardHeading
          title="Portfolio"
          actions={<OverviewViewLink to={`/projects/${slug}/portfolio`} />}
        />
        <CardContent>
          {chosenIds.size === 0 ? (
            <p className="text-sm text-muted-foreground">
              No accounts match this filter.
            </p>
          ) : (
            <>
              <div className="@container">
                <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
                  <div>
                    <MonthFigure
                      figures={figures}
                      isLedgerIncomplete={overview.ledgerIncomplete != null}
                    />
                    <div className="mt-4 flex flex-wrap divide-x divide-border">
                      {figureStrip(figures).map((figure) => (
                        <div
                          key={figure.label}
                          className="px-6 first:pl-0 last:pr-0"
                        >
                          <FigureLabel>{figure.label}</FigureLabel>
                          <FigureValue className="mt-0.5 text-lg">
                            {figure.value}
                          </FigureValue>
                        </div>
                      ))}
                    </div>
                  </div>

                  {figures && figures.allocation.length > 0 ? (
                    <Allocation slices={figures.allocation} />
                  ) : null}
                </div>
              </div>

              {figures && figures.trend.length >= 2 ? (
                <div className="mt-6 border-t border-border pt-4">
                  <FigureLabel className="mb-2">Value over time</FigureLabel>
                  <ValueTrendChart points={figures.trend} />
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
